import React, { useState, useEffect } from 'react';
import { eventService } from '../api/eventService';
import { getDaysDifference } from '../utils/dateUtils';

const EventsProgress = ({ events = [] }) => {
    const [subEventsCache, setSubEventsCache] = useState({});

    // 1. Filter out past/cancelled and get timezone-correct upcoming events
    const upcomingEvents = events.filter(e => {
        if (e.Status === 'Completed' || e.Status === 'Cancelled') return false;
        const days = getDaysDifference(e.StartDateTime || e.StartDate);
        // Upcoming means today (days === 0) or in the future (days > 0)
        return days !== null && days >= 0;
    });

    // 2. Sort by Date (Sooner first)
    upcomingEvents.sort((a, b) => new Date(a.StartDateTime || a.StartDate) - new Date(b.StartDateTime || b.StartDate));

    // 3. Smart Fill Logic
    const MAX_ITEMS = 5;
    const dueToday = [];
    const dueTomorrow = [];
    const dueThisWeek = [];

    for (const e of upcomingEvents) {
        const days = getDaysDifference(e.StartDateTime || e.StartDate);
        if (days === null) continue;

        if (days === 0) {
            dueToday.push(e);
        } else if (days === 1) {
            dueTomorrow.push(e);
        } else if (days > 1 && days <= 7) {
            dueThisWeek.push(e);
        }
    }

    // Determine what to show
    let displayGroups = [];
    let count = 0;

    if (dueToday.length > 0) {
        const take = Math.min(dueToday.length, MAX_ITEMS - count);
        displayGroups.push({ title: "Today", items: dueToday.slice(0, take), color: "#10b981" });
        count += take;
    }

    if (count < MAX_ITEMS && dueTomorrow.length > 0) {
        const take = Math.min(dueTomorrow.length, MAX_ITEMS - count);
        displayGroups.push({ title: "Tomorrow", items: dueTomorrow.slice(0, take), color: "#f59e0b" });
        count += take;
    }

    if (count < MAX_ITEMS && dueThisWeek.length > 0) {
        const take = Math.min(dueThisWeek.length, MAX_ITEMS - count);
        displayGroups.push({ title: "This Week", items: dueThisWeek.slice(0, take), color: "#3b82f6" });
        count += take;
    }

    // 4. Fallback: If nothing in groups, show next 5 upcoming regardless of date
    if (displayGroups.length === 0 && upcomingEvents.length > 0) {
        displayGroups.push({
            title: "Upcoming",
            items: upcomingEvents.slice(0, MAX_ITEMS),
            color: "#64748b"
        });
    }

    // Prepare displayed items list
    const displayedItems = displayGroups.flatMap(g => g.items);
    const displayedEventIdsString = displayedItems.map(e => e.EventID).sort().join(',');

    useEffect(() => {
        let isMounted = true;

        const fetchMissingSubEvents = async () => {
            const missing = displayedItems.filter(e =>
                (typeof e.TotalSubEvents !== 'number' || typeof e.CompletedSubEvents !== 'number')
                && !subEventsCache[e.EventID]
            );

            if (missing.length === 0) return;

            const toSubEventArray = (value) => {
                if (Array.isArray(value)) return value;
                if (value && Array.isArray(value.items)) return value.items;
                if (value && Array.isArray(value.data)) return value.data;
                if (value && Array.isArray(value.SubEvents)) return value.SubEvents;
                if (value && Array.isArray(value.subevents)) return value.subevents;
                return [];
            };

            const updates = {};
            let changed = false;
            for (const e of missing) {
                try {
                    const rawSubs = await eventService.getSubEvents(e.EventID);
                    const subs = toSubEventArray(rawSubs);
                    updates[e.EventID] = {
                        TotalSubEvents: subs.length,
                        CompletedSubEvents: subs.filter(s => s.Status === 'Completed').length
                    };
                    changed = true;
                } catch (err) {
                    console.error("Failed to fetch subevents for EventsProgress", err);
                }
            }

            if (isMounted && changed) {
                setSubEventsCache(prev => ({ ...prev, ...updates }));
            }
        };

        fetchMissingSubEvents();

        return () => { isMounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayedEventIdsString]);

    // Render Helper
    const renderEventGroup = (group) => {
        if (group.items.length === 0) return null;
        return (
            <div key={group.title} style={{ marginBottom: '1.5rem' }}>
                <h5 style={{ margin: '0 0 0.5rem 0', color: group.color, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {group.title}
                </h5>
                {group.items.map(event => {
                    let subProgress = 0;

                    const statusMap = {
                        'New': 10,
                        'Open': 20,
                        'Scheduled': 20,
                        'In Progress': 50,
                        'Review': 80,
                        'Completed': 100,
                        'Cancelled': 0
                    };

                    let totalSub = 0;
                    let completedSub = 0;
                    const cached = subEventsCache[event.EventID];

                    if (cached) {
                        totalSub = cached.TotalSubEvents;
                        completedSub = cached.CompletedSubEvents;
                    } else if (typeof event.TotalSubEvents === 'number' && typeof event.CompletedSubEvents === 'number') {
                        totalSub = event.TotalSubEvents;
                        completedSub = event.CompletedSubEvents;
                    } else if (Array.isArray(event.SubEvents)) {
                        totalSub = event.SubEvents.length;
                        completedSub = event.SubEvents.filter(s => s.Status === 'Completed').length;
                    }

                    // If subevents exist, calculate real progress
                    if (totalSub > 0) {
                        subProgress = Math.round((completedSub / totalSub) * 100);
                    } else {
                        subProgress = statusMap[event.Status] || 0;
                    }

                    return (
                        <div key={event.EventID} style={{ marginBottom: '0.75rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem', gap: '8px' }}>
                                <span 
                                    title={event.Title}
                                    style={{ fontWeight: 600, fontSize: '0.9rem', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}
                                >
                                    {event.Title}
                                </span>
                                <span style={{ fontSize: '0.75rem', color: '#64748b', whiteSpace: 'nowrap', textAlign: 'right' }}>
                                    {new Date(event.StartDateTime).toLocaleDateString([], { month: 'short', day: 'numeric' })} • {new Date(event.StartDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                            <div style={{ width: '100%', height: '6px', backgroundColor: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                                <div style={{
                                    width: `${subProgress}%`,
                                    height: '100%',
                                    backgroundColor: group.color,
                                    borderRadius: '3px',
                                    transition: 'width 0.5s ease-in-out'
                                }} />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            {displayGroups.length === 0 ? (
                <p style={{ color: '#64748b', fontStyle: 'italic' }}>No upcoming events in the next 7 days.</p>
            ) : (
                displayGroups.map(group => renderEventGroup(group))
            )}
        </div>
    );
};


export default EventsProgress;
